export const CALENDARIO_FILTRO_OPTS = [
  { value: 'nada', label: 'Nada (solo disponibilidad)' },
  { value: 'todo', label: 'Todo' },
  { value: 'actuaciones', label: 'Actuaciones (todas)' },
  { value: 'actuaciones_acuerdo', label: 'Actuaciones con acuerdo' },
  { value: 'actuaciones_sin_acuerdo', label: 'Actuaciones sin acuerdo' },
  { value: 'ensayos', label: 'Ensayos' },
];

export function actuacionTieneAcuerdo(actuacion) {
  return !!(actuacion?.eventoId || actuacion?.evento?.id);
}

export function getCalendarioFiltroLabel(filtro) {
  return CALENDARIO_FILTRO_OPTS.find((o) => o.value === filtro)?.label || 'Nada (solo disponibilidad)';
}

/**
 * Aplica filtro de vista y respeta actuaciones/ensayos ocultos en micrositeConfig.
 */
export function applyCalendarioFiltro(
  filtro,
  { actuaciones = [], ensayos = [], hiddenActIds = [], hiddenEnsIds = [] } = {},
) {
  const hiddenActs = new Set(Array.isArray(hiddenActIds) ? hiddenActIds : []);
  const hiddenEns = new Set(Array.isArray(hiddenEnsIds) ? hiddenEnsIds : []);

  const actsVis = (actuaciones || []).filter((a) => a?.id && !hiddenActs.has(a.id));
  const ensVis = (ensayos || []).filter((e) => e?.id && !hiddenEns.has(e.id));

  switch (filtro) {
    case 'nada':
      return { actuaciones: [], ensayos: [] };
    case 'actuaciones':
      return { actuaciones: actsVis, ensayos: [] };
    case 'actuaciones_acuerdo':
      return { actuaciones: actsVis.filter(actuacionTieneAcuerdo), ensayos: [] };
    case 'actuaciones_sin_acuerdo':
      return { actuaciones: actsVis.filter((a) => !actuacionTieneAcuerdo(a)), ensayos: [] };
    case 'ensayos':
      return { actuaciones: [], ensayos: ensVis };
    case 'todo':
    default:
      return { actuaciones: actsVis, ensayos: ensVis };
  }
}

const MONTHS_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

function isValidDate(d) {
  return d instanceof Date && !Number.isNaN(d.getTime());
}

function sortByDateTime(a, b) {
  const da = a?.fecha ? new Date(a.fecha).getTime() : 0;
  const db = b?.fecha ? new Date(b.fecha).getTime() : 0;
  if (da !== db) return da - db;
  return String(a?.horaInicio || '').localeCompare(String(b?.horaInicio || ''));
}

function withinSelectedMonths(e, selectedMonths) {
  if (!selectedMonths || selectedMonths.size === 0) return true;
  if (!e?.fecha) return true;
  const d = new Date(e.fecha);
  if (!isValidDate(d)) return true;
  return selectedMonths.has(d.getMonth() + 1);
}

function eventMonthKey(e) {
  if (!e?.fecha) return null;
  const d = new Date(e.fecha);
  if (!isValidDate(d)) return null;
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${m}`;
}

function monthTitleFromKey(key) {
  const [yStr, mStr] = String(key).split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  if (!y || !m) return key;
  const name = MONTHS_ES[m - 1] || '';
  return `${name.charAt(0).toUpperCase()}${name.slice(1)} ${y}`;
}

/**
 * Lista plana de ítems para export / resumen, respetando meses de msConfig.
 */
export function buildCalendarioExportItems({ actuaciones, ensayos, msConfig }) {
  const selectedMonths = new Set(
    (Array.isArray(msConfig?.calendarioMeses) ? msConfig.calendarioMeses : [])
      .filter((m) => Number.isInteger(m) && m >= 1 && m <= 12),
  );

  const acts = (actuaciones || [])
    .filter((a) => withinSelectedMonths(a, selectedMonths))
    .slice()
    .sort(sortByDateTime);
  const ens = (ensayos || [])
    .filter((e) => withinSelectedMonths(e, selectedMonths))
    .slice()
    .sort(sortByDateTime);

  const monthKeys = new Set();
  for (const a of acts) {
    const k = eventMonthKey(a);
    if (k) monthKeys.add(k);
  }
  for (const e of ens) {
    const k = eventMonthKey(e);
    if (k) monthKeys.add(k);
  }

  const groups = Array.from(monthKeys).sort().map((key) => {
    const monthActs = acts.filter((a) => eventMonthKey(a) === key);
    const monthEns = ens.filter((e) => eventMonthKey(e) === key);
    const items = [];
    for (const a of monthActs) {
      items.push({ kind: 'actuacion', raw: a });
    }
    for (const e of monthEns) {
      items.push({ kind: 'ensayo', raw: e });
    }
    items.sort((x, y) => sortByDateTime(x.raw, y.raw));
    return { key, title: monthTitleFromKey(key), items };
  });

  return { groups, totalItems: groups.reduce((n, g) => n + g.items.length, 0) };
}

export function formatCalendarioFecha(fecha) {
  if (!fecha) return '';
  const d = new Date(fecha);
  if (!isValidDate(d)) return '';
  return d.toLocaleDateString('es-ES', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatCalendarioHora(item) {
  if (!item?.horaInicio) return '';
  return `${item.horaInicio}${item.horaFin ? `–${item.horaFin}` : ''}`;
}

export function calendarioItemTitulo(item, kind) {
  if (kind === 'ensayo') {
    return item?.descripcion || item?.grupo?.nombre || 'Ensayo';
  }
  return item?.nombre || 'Actuación';
}

/** Convierte fecha de actuación/ensayo a YYYY-MM-DD local. */
export function actividadFechaYmd(fecha) {
  if (!fecha) return null;
  const d = new Date(fecha);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Mapa fecha → { actuaciones, ensayos } para pintar en celdas del calendario.
 */
export function buildActividadesPorFecha(actuaciones = [], ensayos = []) {
  const map = new Map();
  const ensure = (key) => {
    if (!map.has(key)) map.set(key, { actuaciones: [], ensayos: [] });
    return map.get(key);
  };
  for (const a of actuaciones) {
    const k = actividadFechaYmd(a.fecha);
    if (k) ensure(k).actuaciones.push(a);
  }
  for (const e of ensayos) {
    const k = actividadFechaYmd(e.fecha);
    if (k) ensure(k).ensayos.push(e);
  }
  return map;
}

export function calendarioItemTipoLabel(kind, item) {
  if (kind === 'ensayo') return 'Ensayo';
  if (actuacionTieneAcuerdo(item)) return 'Actuación (con acuerdo)';
  return 'Actuación';
}
